import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { logger } from '../../utils/logger';

interface StorageMetadata {
  sourceModule: string;
  docType: string;
  visibility: string;
  createdBy: string;
  tags?: string[];
  [key: string]: any;
}

interface StorageItem {
  name: string;
  id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_accessed_at?: string | null;
  metadata?: StorageMetadata | null;
  objectKey?: string;
}

const FileManager: React.FC = () => {
  const { getToken } = useAuth();
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [newFolder, setNewFolder] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [usedBytes, setUsedBytes] = useState(0);

  const BYTES_50MB = 50 * 1024 * 1024;
  const BYTES_10GB = 10 * 1024 * 1024 * 1024;

  const breadcrumbs = useMemo(() => {
    if (!prefix) return [{ name: 'Home', path: '' }];
    const parts = prefix.split('/').filter(Boolean);
    const crumbs = [{ name: 'Home', path: '' }];
    let currentPath = '';
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      crumbs.push({ name: part, path: currentPath });
    }
    return crumbs;
  }, [prefix]);

  // Simple fetch function that follows LogoGenerator pattern
  const makeRequest = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const userId = await getToken();
    if (!userId) {
      throw new Error('Authentication required');
    }

    const url = `${ENV.API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // For GET requests, add userId to query params
    if (!options.method || options.method === 'GET') {
      const urlWithParams = new URL(url);
      urlWithParams.searchParams.set('userId', userId);
      return fetch(urlWithParams.toString(), { ...options, headers });
    }

    // For POST/PUT/DELETE with JSON body, add userId to body
    if (options.body && typeof options.body === 'string') {
      try {
        const payload = JSON.parse(options.body);
        payload.userId = userId;
        return fetch(url, { ...options, headers, body: JSON.stringify(payload) });
      } catch {
        // If not JSON, just send as is
        return fetch(url, { ...options, headers });
      }
    }

    // For FormData (file uploads), add userId to form data AND query for robustness
    if (options.body instanceof FormData) {
      options.body.set('userId', userId);
      console.log('makeRequest: Added userId to FormData:', userId);
      // Also append userId in query so backend can read it even if body parsing fails
      const urlWithParams = new URL(url);
      urlWithParams.searchParams.set('userId', userId);
      // Log FormData in development only
      logger.debug('FormData upload', {
        entries: Array.from(options.body.entries()).map(([k, v]) => ({
          key: k,
          value: v instanceof File ? `File(${v.name})` : v
        }))
      });
      // Remove Content-Type header for FormData (browser sets it with boundary)
      const { 'Content-Type': _, ...headersWithoutContentType } = headers;
      return fetch(urlWithParams.toString(), { ...options, headers: headersWithoutContentType });
    }

    // Default case
    const body = JSON.stringify({ userId });
    return fetch(url, { ...options, headers, body });
  }, [getToken]);

  const loadUsage = useCallback(async () => {
    try {
      const res = await makeRequest('/api/storage/usage');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setUsedBytes(Number(data.usedBytes || 0));
    } catch (e: any) {
      toast.error('Failed to fetch storage usage');
    }
  }, [makeRequest]);

  const list = useCallback(async (p: string) => {
    setLoading(true);
    try {
      const endpoint = p ? `/api/storage/list?prefix=${encodeURIComponent(p)}` : '/api/storage/list';
      const res = await makeRequest(endpoint);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems(data.items || []);
    } catch (e: any) {
      toast.error('Failed to list files');
    } finally {
      setLoading(false);
    }
  }, [makeRequest]);

  const initPresets = useCallback(async () => {
    try {
      const res = await makeRequest('/api/storage/init-presets', { method: 'POST', body: JSON.stringify({}) });
      if (!res.ok) throw new Error(await res.text());
    } catch (_) {
      // non-blocking
    }
  }, [makeRequest]);

  const createFolder = useCallback(async () => {
    console.log('createFolder called, newFolder:', newFolder);
    const name = newFolder.trim();
    if (!name) {
      toast.error('Please enter a folder name');
      return;
    }
    // Prevent directory traversal
    if (name.includes('..') || name.startsWith('/')) {
      toast.error('Invalid folder name');
      return;
    }
    try {
      const path = prefix ? `${prefix}/${name}` : name;
      console.log('Creating folder at path:', path);
      const res = await makeRequest('/api/storage/create-folder', {
        method: 'POST',
        body: JSON.stringify({ path }),
      });
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Create folder error:', errorText);
        throw new Error(errorText);
      }
      toast.success('Folder created');
      setNewFolder('');
      list(prefix);
    } catch (e: any) {
      console.error('Create folder exception:', e);
      toast.error(`Failed to create folder: ${e.message}`);
    }
  }, [makeRequest, list, newFolder, prefix]);

  const inferDocType = useCallback((mime: string) => {
    if (!mime) return 'document';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  }, []);

  const uploadFile = useCallback(async () => {
    console.log('uploadFile called, file:', file);
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    if (file.size > BYTES_50MB) {
      toast.error('File exceeds 50MB limit');
      return;
    }
    // Quota precheck (best-effort; backend enforces as well)
    if (usedBytes + file.size > BYTES_10GB) {
      toast.error('Uploading would exceed 10GB quota');
      return;
    }
    const userId = await getToken();
    if (!userId) {
      toast.error('Authentication required');
      return;
    }
    try {
      console.log('Starting upload process...');
      const form = new FormData();
      const dest = prefix ? `${prefix}/${file.name}` : file.name;
      form.set('path', dest);
      form.set('file', file);

      const metadata: StorageMetadata = {
        sourceModule: 'file-manager',
        docType: inferDocType(file.type || ''),
        visibility: 'private',
        createdBy: userId,
        tags: ['manual-upload'],
        filename: file.name,
      };
      form.set('metadata', JSON.stringify(metadata));
      
      logger.debug('Starting file upload', { destination: dest, filename: file.name });
      const res = await makeRequest('/api/storage/upload', {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const text = await res.text();
        logger.error('Upload error:', text);
        throw new Error(text);
      }
      toast.success('Upload complete');
      setFile(null);
      await Promise.all([loadUsage(), list(prefix)]);
    } catch (e: any) {
      logger.error('Upload exception:', e);
      const msg = String(e?.message || 'Upload failed');
      if (msg.includes('413')) {
        toast.error('File too large (max 50MB)');
      } else if (msg.toLowerCase().includes('quota')) {
        toast.error('Storage quota exceeded');
      } else {
        toast.error(`Upload failed: ${msg}`);
      }
    }
  }, [file, loadUsage, list, prefix, usedBytes, BYTES_50MB, BYTES_10GB, makeRequest]);

  const deleteItem = useCallback(async (name: string, isFolder: boolean) => {
    try {
      const path = prefix ? `${prefix}/${name}` : name;
      const res = await makeRequest('/api/storage/object', {
        method: 'DELETE',
        body: JSON.stringify({ path: isFolder ? `${path}/` : path, recursive: isFolder }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Deleted');
      await Promise.all([loadUsage(), list(prefix)]);
    } catch (e: any) {
      toast.error('Delete failed');
    }
  }, [makeRequest, list, loadUsage, prefix]);

  useEffect(() => {
    // Ensure preset folders exist; non-blocking
    initPresets();
  }, [initPresets]);

  useEffect(() => {
    list(prefix);
    loadUsage();
  }, [list, loadUsage, prefix]);

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">My Files</h1>
      <p className="text-sm text-gray-600 mb-6">Manage up to 10GB of files. Max upload size per file is 50MB.</p>

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-700">
          <strong>Usage:</strong> {(usedBytes / (1024*1024)).toFixed(1)} MB / { (BYTES_10GB / (1024*1024*1024)).toFixed(0)} GB
        </div>
        <nav className="text-sm">
          {breadcrumbs.map((c, idx) => (
            <span key={c.path}>
              <button className="text-blue-600 hover:underline" onClick={() => setPrefix(c.path)}>{c.name}</button>
              {idx < breadcrumbs.length - 1 && ' / '}
            </span>
          ))}
        </nav>
      </div>

      <div className="flex gap-3 items-center mb-6">
        <input
          type="text"
          placeholder="New folder name"
          className="border rounded px-3 py-2"
          value={newFolder}
          onChange={(e) => setNewFolder(e.target.value)}
        />
        <button onClick={createFolder} className="bg-blue-600 text-white px-4 py-2 rounded">Create Folder</button>

        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="ml-4"
        />
        <button onClick={uploadFile} className="bg-green-600 text-white px-4 py-2 rounded" disabled={!file || loading}>
          Upload
        </button>
      </div>

      <div className="bg-white shadow rounded">
        {loading ? (
          <div className="p-6">Loading...</div>
        ) : (
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-gray-600 border-b">
                <th className="p-3">Name</th>
                <th className="p-3">Doc Type</th>
                <th className="p-3">Source</th>
                <th className="p-3">Tags</th>
                <th className="p-3 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Folders */}
              {items.filter(i => i.name && i.name.endsWith('/')).map((it) => (
                <tr key={`folder-${it.name}`} className="border-b">
                  <td className="p-3">
                    <button className="text-blue-600 hover:underline" onClick={() => setPrefix(prefix ? `${prefix}/${it.name.slice(0, -1)}` : it.name.slice(0, -1))}>
                      📁 {it.name.slice(0, -1)}
                    </button>
                  </td>
                  <td className="p-3 text-gray-500">—</td>
                  <td className="p-3 text-gray-500">—</td>
                  <td className="p-3 text-gray-500">—</td>
                  <td className="p-3">
                    <button className="text-red-600" onClick={() => deleteItem(it.name.slice(0, -1), true)}>Delete</button>
                  </td>
                </tr>
              ))}
              {/* Files */}
              {items.filter(i => i.name && !i.name.endsWith('/')).map((it) => (
                <tr key={`file-${it.name}`} className="border-b">
                  <td className="p-3">📄 {it.name}</td>
                  <td className="p-3 text-gray-600">{it.metadata?.docType || '—'}</td>
                  <td className="p-3 text-gray-600">{it.metadata?.sourceModule || '—'}</td>
                  <td className="p-3 text-gray-600">{it.metadata?.tags?.length ? it.metadata.tags.join(', ') : '—'}</td>
                  <td className="p-3">
                    <button className="text-red-600" onClick={() => deleteItem(it.name, false)}>Delete</button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="p-6 text-gray-500" colSpan={5}>No items</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FileManager;
